import { Cancel, Edit, UploadFile } from '@mui/icons-material'
import { Box, Button, Card, CardContent, IconButton, Typography, useMediaQuery } from '@mui/material'
import { blue } from '@mui/material/colors'
import React from 'react'
import { useDropzone } from 'react-dropzone'
import { setOpacity } from '../../../utils/setOpacity'
import { colors, spacingItem, theme } from '../../theme'
import { getAdoptionAttemptDocumentsSrc } from '../../../utils/S3-links'
import { useMutation } from 'react-query'
import { AdoptersClient } from '../../../interactors/clients/AdoptersClient'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { LoadingButton } from '@mui/lab'
import { AdoptionAttemptDocumentDtoType } from '../../../interactors/gen/backendClient'
import { addPrimaryFabSx, addSecondaryFabSx } from '../../common/addPrimaryCtaSx'
import { getSoftChipSx } from '../../common/chipStyles'

interface Props {
  title: string
  adopterId: string
  adoptionAttemptId: string
  type: AdoptionAttemptDocumentDtoType
  originalFileKey?: string
  initialFileKey?: string
  disabled?: boolean
  statusLabel?: string
  statusColor?: string
}

export const ExitDocumentCard: React.FC<Props> = ({
  title,
  adopterId,
  adoptionAttemptId,
  type,
  originalFileKey,
  initialFileKey,
  disabled = false,
  statusLabel,
  statusColor,
}) => {
  const [fileKey, setFileKey] = React.useState<string | undefined>(originalFileKey)
  const [isEditing, setIsEditing] = React.useState<boolean>(false)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('md'))

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    noClick: false,
    noKeyboard: false,
    onDrop: (acceptedFiles) => {
      setSelectedFile(acceptedFiles[0])
    },
  })

  const globalSnackbar = useGlobalSnackbarStore()

  const uploadFileMutation = useMutation(
    async ({ file }: { file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', type)

      return await AdoptersClient.addDocumentToAdoptionAttempt(adopterId, adoptionAttemptId, formData)
    },
    {
      onSuccess: (data) => {
        setFileKey(data)
        setIsEditing(false)
        setSelectedFile(null)
        globalSnackbar.triggerSuccessMessage(`Votre document a bien été ajouté !`)
      },
    }
  )

  const handleEditClick = () => {
    setIsEditing((prev) => !prev)
    setSelectedFile(null)
  }

  const handleFileUpload = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (selectedFile) {
      uploadFileMutation.mutate({ file: selectedFile })
    }
  }

  const isHandDelivered = fileKey === 'NOT_PROVIDED.pdf'
  const hasConsultableFile = Boolean(fileKey && fileKey !== 'NOT_PROVIDED.pdf')
  const shortTitle =
    title === "Certificat d'engagement" ? 'Certificat' : title === "Contrat d'adoption" ? 'Contrat' : title
  const initialDocumentHref =
    initialFileKey && initialFileKey !== 'NOT_PROVIDED.pdf' ? getAdoptionAttemptDocumentsSrc(initialFileKey) : undefined
  const descriptionText = isHandDelivered
    ? 'Ce document a été fourni en mains propres.'
    : type === 'knowledge-certificate' || type === 'adoption-contract'
    ? "La signature de l'adoptant a été validée."
    : "Ce document a bien été partagé avec l'adoptant."

  const renderInitialDocumentLink = () => {
    if (!initialDocumentHref || isCompactScreen) {
      return null
    }

    return (
      <Box
        component="a"
        href={initialDocumentHref}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: colors.cello,
          fontSize: 12.5,
          fontWeight: 700,
          lineHeight: '28px',
          textDecoration: 'underline',
          textUnderlineOffset: '2px',
          whiteSpace: 'nowrap',
          '&:hover': {
            color: colors.cello,
          },
        }}
      >
        Document initial
      </Box>
    )
  }

  return (
    <Card
      sx={{
        border: '1px solid rgba(122, 129, 151, 0.16)',
        borderRadius: { xs: 0, md: 4 },
        boxShadow: { xs: 'none', md: '0 8px 22px rgba(26, 43, 77, 0.05)' },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 0, sm: 2 },
          height: { sm: '100%' },
          padding: { xs: '18px !important', md: '9px 18px !important' },
          '&:last-child': { pb: { xs: '18px', md: '9px' } },
        }}
      >
        {(isEditing || !fileKey) && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography
                variant="h5"
                component="p"
                sx={{ fontSize: { xs: 16, sm: 18 }, color: '#1F2D4D' }}
                fontWeight={700}
              >
                {isCompactScreen ? shortTitle : title}
              </Typography>
              {statusLabel && (
                <Box
                  component="span"
                  sx={getSoftChipSx(statusColor, {
                    height: 28,
                    fontSize: 12,
                    labelWeight: 600,
                    emphasis: true,
                    maxWidth: 220,
                  })}
                >
                  <Typography
                    sx={{
                      px: 1.25,
                      fontSize: 12,
                      fontWeight: 600,
                      lineHeight: '28px',
                      whiteSpace: 'nowrap',
                      color: '#203864',
                    }}
                  >
                    {statusLabel}
                  </Typography>
                </Box>
              )}
              {renderInitialDocumentLink()}
            </Box>
            {!isMobile && isEditing && (
              <IconButton sx={{ p: 0 }} onClick={() => handleEditClick()} disabled={disabled}>
                {isEditing ? <Cancel /> : <Edit />}
              </IconButton>
            )}
          </Box>
        )}

        {!isEditing && fileKey && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              gap: { xs: 1.5, md: 2 },
              mt: 0,
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.35 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography
                  variant="h5"
                  component="p"
                  sx={{ fontSize: { xs: 16, sm: 18 }, color: '#1F2D4D' }}
                  fontWeight={700}
                >
                  {isCompactScreen ? shortTitle : title}
                </Typography>
                {statusLabel && (
                  <Box
                    component="span"
                    sx={getSoftChipSx(statusColor, {
                      height: 28,
                      fontSize: 12,
                      labelWeight: 600,
                      emphasis: true,
                      maxWidth: 220,
                    })}
                  >
                    <Typography
                      sx={{
                        px: 1.25,
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: '28px',
                        whiteSpace: 'nowrap',
                        color: '#203864',
                      }}
                    >
                      {statusLabel}
                    </Typography>
                  </Box>
                )}
                {renderInitialDocumentLink()}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: colors.cello,
                  fontSize: { xs: '13px', sm: '13.5px' },
                  fontStyle: 'italic',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                {descriptionText}
              </Typography>
            </Box>
            <Box
              sx={{
                width: { xs: '100%', md: 'auto' },
                minWidth: { md: 280 },
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: 'nowrap',
                justifyContent: { xs: 'stretch', md: 'flex-end' },
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 0.75,
                alignSelf: { xs: 'stretch', md: 'center' },
              }}
            >
              <Button
                variant={isHandDelivered ? 'outlined' : 'text'}
                sx={{
                  ...addSecondaryFabSx,
                  minHeight: 36,
                  height: 36,
                  px: { xs: 1.5, sm: 1.75 },
                  whiteSpace: 'nowrap',
                  flex: { xs: 1, md: '0 1 auto' },
                  ...(isHandDelivered
                    ? {}
                    : {
                        borderColor: 'transparent',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(232, 242, 251, 0.72)',
                          borderColor: 'transparent',
                        },
                      }),
                }}
                onClick={() => handleEditClick()}
                disabled={disabled}
              >
                {isHandDelivered ? 'Ajouter' : 'Remplacer'}
              </Button>
              {hasConsultableFile && (
                <Button
                  component="a"
                  href={getAdoptionAttemptDocumentsSrc(fileKey)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{
                    ...addSecondaryFabSx,
                    minHeight: 36,
                    height: 36,
                    px: { xs: 1.75, sm: 2 },
                    whiteSpace: 'nowrap',
                    flex: { xs: 1, md: '0 1 auto' },
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Consulter
                </Button>
              )}
            </Box>
          </Box>
        )}

        {isMobile && (!fileKey || isEditing) && (
          <Box {...getRootProps()}>
            <Typography fontSize={14} mb={1}>
              {selectedFile?.name || 'Aucun fichier sélectionné'}
            </Typography>
          </Box>
        )}

        {/* Buttons */}
        {(!fileKey || isEditing) && (
          <Box display="flex" gap={1}>
            {/* Buttons: "Remplacer" (both screens) / "Annuler" (only for mobile) */}
            {(fileKey || isEditing) &&
              // Mobile
              (isMobile ? (
                <Button
                  variant="outlined"
                  sx={{
                    ...addSecondaryFabSx,
                    width: fileKey === 'NOT_PROVIDED.pdf' ? '100%' : isEditing ? '100%' : '50%',
                    minHeight: 38,
                    height: 38,
                  }}
                  onClick={() => handleEditClick()}
                  disabled={disabled}
                >
                  {isEditing ? 'Annuler' : fileKey === 'NOT_PROVIDED.pdf' ? 'Ajouter' : 'Remplacer'}
                </Button>
              ) : (
                // Desktop
                !isEditing && (
                  <Button
                    variant="outlined"
                    sx={{ textTransform: 'none', fontSize: { xs: 14, sm: 16 }, width: isEditing ? '100%' : '50%' }}
                    onClick={() => handleEditClick()}
                    disabled={disabled}
                  >
                    {fileKey === 'NOT_PROVIDED.pdf' ? 'Ajouter' : 'Remplacer'}
                  </Button>
                )
              ))}

            {/* Buttons: "Parcourir" (only for mobile) / "Valider" (both screens) */}
            {(!fileKey || isEditing) &&
              // Mobile
              (isMobile ? (
                <Box
                  {...getRootProps()}
                  // width={
                  //   (type === 'adoption-contract' || type === 'knowledge-certificate') && isEditing ? '100%' : '100%'
                  // }
                  width="100%"
                >
                  <input {...getInputProps()} />
                  {selectedFile ? (
                    <LoadingButton
                      variant="contained"
                      sx={{ ...addPrimaryFabSx, width: '100%', fontSize: { xs: 14, sm: 15 } }}
                      loading={uploadFileMutation.isLoading}
                      disabled={disabled}
                      onClick={(e) => handleFileUpload(e)}
                    >
                      Valider
                    </LoadingButton>
                  ) : (
                    <Button
                      variant="outlined"
                      sx={{
                        ...addSecondaryFabSx,
                        width: '100%',
                        fontSize: { xs: 14, sm: 15 },
                      }}
                      disabled={disabled}
                    >
                      Parcourir
                    </Button>
                  )}
                </Box>
              ) : (
                // Desktop
                <Box
                  width="100%"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  padding={3}
                  {...getRootProps({
                    sx: {
                      ...spacingItem,
                      border: 3,
                      borderRadius: 5,
                      borderStyle: 'dashed',
                      borderColor: isDragActive ? 'green' : blue,
                      backgroundColor: isDragActive
                        ? 'lightgreen'
                        : acceptedFiles[0]
                        ? setOpacity(colors.geyser, 0.4)
                        : 'transparent',
                      transition: 'all 0.3s',
                    },
                  })}
                >
                  <input {...getInputProps()} />
                  <UploadFile sx={{ color: blue, fontSize: 64, mb: 1 }} />
                  <Typography variant="h4" component="p" fontSize={18} textAlign="center">
                    Glissez-déposer ou
                    <br />
                    Insérer un document
                  </Typography>
                  <Box
                    marginTop={4}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 1,
                      maxWidth: '100%',
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="p"
                      fontSize={16}
                      textAlign="center"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Votre document:
                    </Typography>
                    <Typography
                      variant="h4"
                      component="p"
                      fontSize={14}
                      textAlign="center"
                      color={colors.black}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '120%',
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                      }}
                    >
                      {selectedFile?.name || 'Aucun fichier sélectionné'}
                    </Typography>
                  </Box>
                  {selectedFile ? (
                    <LoadingButton
                      variant="contained"
                      sx={{
                        ...addPrimaryFabSx,
                        fontSize: { xs: 14, sm: 15 },
                        width: '50%',
                        mt: 1,
                      }}
                      loading={uploadFileMutation.isLoading}
                      disabled={disabled}
                      onClick={(e) => handleFileUpload(e)}
                    >
                      Valider
                    </LoadingButton>
                  ) : (
                    <Button
                      variant="outlined"
                      sx={{
                        ...addSecondaryFabSx,
                        fontSize: { xs: 14, sm: 15 },
                        width: '50%',
                        mt: 1,
                      }}
                      disabled={disabled}
                    >
                      Parcourir
                    </Button>
                  )}
                </Box>
              ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
